import Grid from "@components/Grid/Grid";
import { useEffect, useState } from "react";

import CoveoSearchFacet from "@components/CoveoSearchFacet/CoveoSearchFacet";
import Cta from "@components/Cta/Cta";
import {
  AtomicBreadbox,
  AtomicDidYouMean,
  AtomicFacetManager,
  AtomicLayoutSection,
  AtomicNoResults,
  AtomicQueryError,
  AtomicQuerySummary,
  AtomicResultList,
  AtomicResultsPerPage,
  AtomicSearchBox,
  AtomicSearchBoxQuerySuggestions,
  AtomicSearchLayout,
  AtomicSortDropdown,
  AtomicSortExpression,
  Result,
} from "@coveo/atomic-react";
import type { SearchEngine } from "@coveo/headless";
import { CoveoPager } from "../CoveoPager";
import { CoveStyles } from "../CoveoSeachComponentStyles";
import { Facet, SortOption } from "../CoveoSearchComponent";
import { CoveoDPOSearchResultsStyles } from "./CoveoDPOSearchResultsStyles";

export interface dpoSettingsProps {
  keyDetailsHeading?: string;
  fundedByLabel?: string;
  valueLabel?: string;
  openToLabel?: string;
  programmesLabel?: string;
}

export interface DPOPageProps {
  engine: SearchEngine;
  resultsPerPage?: string;
  showSearchBar?: boolean;
  redirectUrl?: string;
  facets?: Facet[];
  sortOptions?: SortOption[];
  displayPagination?: boolean;
  isMobile?: boolean;
  resultsFocusRef?: React.RefObject<HTMLDivElement>;
}

export const CoveoDPOSearchResults = (props: DPOPageProps) => {
  const [showFilters, setShowFilters] = useState(false);

  const {
    engine,
    resultsPerPage,
    showSearchBar,
    redirectUrl,
    facets,
    sortOptions,
    displayPagination,
    isMobile,
    resultsFocusRef,
  } = props;

  const TemplateResovler = (_result: Result) => {
    const result = _result as Result & {
      raw: Result["raw"] & {
        dpofundingtype: string;
        dpotitle: string;
        dpovaluetext: string;
        dpoopento: string;
      };
    };

    return (
      <>
        <style>{CoveoDPOSearchResultsStyles(isMobile)}</style>
        <div className="funding-provider-tile base6">
          <a href={result.clickUri} className="funding-provider">
            <div className="funding-provider-labels">
              {" "}
              <span className="funding-provider-type">
                {result.raw.dpofundingtype}
              </span>
            </div>{" "}
            <h3 className="tile-title">{result.raw.dpotitle}</h3>{" "}
            <dl>
              {result.raw.dpovaluetext ? (
                <div className="value-label">
                  <dt>Value: </dt>
                  <dd>{result.raw.dpovaluetext}</dd>
                </div>
              ) : null}
              <div className="open-to-label">
                <dt>Open to: </dt>
                <dd>{result.raw.dpoopento}</dd>
              </div>
            </dl>
          </a>
        </div>
      </>
    );
  };
  const FullWidth = {
    width: "100%",
    maxWidth: "1250px",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gridTemplateColumns: "25% 1fr",
    marginTop: "56px",
  };

  const subTitle = {
    color: "#001e62",
    fontFamily: "var(--lbs-heading-font-family),serif",
    fontSize: "1.25rem",
    lineHeight: "1.2",
    marginBottom: "13px",
    display: "block",
  };

  useEffect(() => {
    if (isMobile) {
      setShowFilters(false);
    }
  }, [isMobile]);

  return (
    <AtomicSearchLayout style={FullWidth}>
      <style>{CoveStyles(isMobile, 3)}</style>
      <style>{CoveoDPOSearchResultsStyles(isMobile)}</style>
      <style>
        {`
          atomic-facet::part(value-count) {
            display: none;
          }
        `}
      </style>
      <Grid row>
        <AtomicResultsPerPage
          choicesDisplayed={resultsPerPage || "9"}
        ></AtomicResultsPerPage>
        <Grid column sm={12} md={4} lg={4}>
          {showSearchBar ? (
            <AtomicLayoutSection section="search">
              <span style={subTitle}>Refine</span>
              {isMobile ? (
                <Cta
                  url=""
                  onClick={() => setShowFilters(!showFilters)}
                  type="primary"
                  linkText={showFilters ? "Filter results" : "Apply filters"}
                />
              ) : (
                <></>
              )}
              {(showFilters || !isMobile) && (
                <>
                  <p style={{ marginTop: "16px" }}>
                    Know which scholarship, loan, or funding provider you are
                    looking for?
                  </p>
                  <AtomicSearchBox
                    redirectionUrl={redirectUrl || ""}
                    numberOfQueries={5}
                  >
                    <AtomicSearchBoxQuerySuggestions />
                  </AtomicSearchBox>
                </>
              )}
            </AtomicLayoutSection>
          ) : null}

          {facets ? (
            <div
              style={{ display: !showFilters && isMobile ? "none" : "block" }}
            >
              <AtomicLayoutSection section="facets">
                <AtomicFacetManager>
                  {facets.map((facet) => (
                    <CoveoSearchFacet
                      key={facet.fields.coveoFieldName}
                      content={facet.fields}
                    />
                  ))}
                </AtomicFacetManager>
              </AtomicLayoutSection>
            </div>
          ) : null}
        </Grid>
        {!showFilters && (
          <Grid column sm={12} md={8} lg={8}>
            <AtomicLayoutSection section="main">
              <AtomicLayoutSection section="status">
                <div className="coveo-status">
                  <Grid row alignItems={"center"}>
                    <Grid column sm={5} md={6}>
                      <AtomicQuerySummary />
                    </Grid>

                    <Grid column sm={7} md={6}>
                      {sortOptions && (
                        <AtomicSortDropdown>
                          {sortOptions.map((option, i) => (
                            <AtomicSortExpression
                              key={`${option.fields?.label}${i}`}
                              expression={option?.fields.expression}
                              label={option?.fields.label}
                            ></AtomicSortExpression>
                          ))}
                        </AtomicSortDropdown>
                      )}
                    </Grid>
                  </Grid>
                </div>

                <AtomicBreadbox style={{ marginBottom: "20px" }} />
                <AtomicDidYouMean />
              </AtomicLayoutSection>
              <AtomicLayoutSection section="results">
                <h2 ref={resultsFocusRef} tabIndex={-1} className="sr-only">
                  Search results
                </h2>
                <AtomicResultList
                  display="grid"
                  imageSize="small"
                  template={TemplateResovler}
                />

                <AtomicQueryError />
                <AtomicNoResults />
              </AtomicLayoutSection>
              {displayPagination && (
                <AtomicLayoutSection section="pagination">
                  <CoveoPager
                    engine={engine}
                    resultsFocusRef={resultsFocusRef}
                  />
                </AtomicLayoutSection>
              )}
            </AtomicLayoutSection>
          </Grid>
        )}
      </Grid>
    </AtomicSearchLayout>
  );
};

export default CoveoDPOSearchResults;
